import { IImportable } from 'library/ts/interfaces/importable/IImportable';
import { ISkinArgument } from 'skin/ts/Skin/interfaces/arguments/ISkinArgument';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { IEmbedManagerArgument } from 'tag/ts/tag/interfaces/arguments/IEmbedManagerArgument';
import { ITagEmbedManager } from 'tag/ts/tag/interfaces/player/ITagEmbedManager';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { SkinMode } from 'tag/ts/tag/model/type/SkinMode';
import { TagDomAttribute } from 'tag/ts/tag/model/type/TagDomAttribute';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Smart Tag embed orchestration and platform integration manager
 *
 * **Embed Integration Architecture:**
 * EmbedManager orchestrates Smart Tag's integration into various hosting environments,
 * managing the complete embed lifecycle from initialization through player container
 * setup and skin integration. Coordinates between Smart Tag core and hosting applications.
 *
 * **Core Responsibilities:**
 * - **Player Container Management**: Provides appropriate DOM containers for different player types
 * - **Skin Integration**: Coordinates Desktop skin applications with Smart Tag player infrastructure
 * - **Platform Adaptation**: Handles embed differences between Desktop, Mobile Web, and Smart TV
 * - **Lifecycle Orchestration**: Manages initialization sequence and ready state coordination
 * - **Cross-Component Communication**: Bridges Smart Tag components with external applications
 *
 * **Player Type Coordination:**
 * - **Chromeless Player**: Manages skin-provided containers for custom UI implementations
 * - **Full Player**: Coordinates VTG's native UI with Smart Tag business logic
 * - **Skinless Mode**: Direct Smart Tag container integration without external skin
 *
 * **Embed Environment Support:**
 * - **Desktop/Mobile Web**: Full skin integration with Chromeless/Full player support
 * - **Smart TV (HTML5)**: Streamlined integration optimized for TV platform constraints
 * - **Third-Party Integration**: Provides container and lifecycle hooks for external applications
 *
 * **Integration Flow:**
 * External Application → EmbedManager → Smart Tag Core → Player Container → Video Playback
 *
 * **Skin Integration Strategy:**
 * Coordinates between Smart Tag's video logic and external skin applications, ensuring
 * proper container management, event coordination, and lifecycle synchronization for
 * seamless user experience across different UI implementations.
 *
 * **Business Impact:**
 * Enables Smart Tag deployment across diverse hosting environments while maintaining
 * consistent video functionality and user experience standards.
 */
export class EmbedManager extends AbstractTagClass implements ITagEmbedManager {

	/**
	 * @description Holds player container reference
	 */
	#playerContainer: HTMLElement;

	/**
	 * @description Promise that resolves when ON_SESSION_START_IMPORTS_DONE event fires
	 */
	#importsCompletedPromise: Promise<void>;

	constructor( argument: IEmbedManagerArgument ) {
		super( { tag: argument?.tag, name: 'EmbedManager' } );

		// Setup promise to capture ON_SESSION_START_IMPORTS_DONE event
		// This must be done BEFORE loadDependencies() is called to avoid race condition
		this.#importsCompletedPromise = new Promise( ( resolve ) => {
			this.tag.eventDispatcher.addEventListener(
				{ type: TagEvent.ON_SESSION_START_IMPORTS_DONE, timesToDispatch: 1 },
				() => {
					resolve();
				}
			);
		} );

		// Setup tag events handler for metadata ready (needed for player creation)
		// AND for custom skin ready (needed to capture player container from skin)
		this.tagEventsHandler(
			this.#onTagEvent,
			[
				TagEvent.ON_METADATA_READY,
				TagEvent.ON_CUSTOM_SKIN_READY // CRITICAL: Must listen for skin ready to capture playerContainer
			]
		);
	}

	/**
	 * @description Returns player container DOM element per player type
	 * @return {HTMLElement}
	 */
	get playerContainer(): HTMLElement {
		// if skin is disabled => execute on ready event...
		if ( this?.params?.SKIN?.enabled === false ) {
			// pull player container for skin-less setup
			this.#playerContainer = this.tag?.view?.smartTagContentElement;
			return this.#playerContainer;
		}

		// filter by skin engine
		switch ( this?.tag?.model?.skinMode ) {
			case SkinMode.DESKTOP:
			case SkinMode.CUSTOM_MOBILE:
				// player container was passed by skin application set on TagEvent.ON_CUSTOM_SKIN_READY event
				return this.#playerContainer;
			// break;
			case SkinMode.AVIA_UI:
				// pull player container for Avia JS UI version of player... element with attribute smart-tag-content='uuid'
				this.#playerContainer = this.tag?.view?.smartTagContentElement;
				break;
			case SkinMode.NONE:
				// no Smart Tag skin at all — same skin-less container as the SKIN.enabled === false path above
				this.#playerContainer = this.tag?.view?.smartTagContentElement;
				break;
		}
		return this.#playerContainer;
	}

	/**
	 * @description Wait for EmbedManager to be ready
	 * Coordinates imports completion, skin initialization, and final ready event
	 * @returns {Promise<void>}
	 */
	waitForReady = async (): Promise<void> => {
		try {
			this?.logger?.log( 'waitForReady: Starting EmbedManager coordination' );

			// Wait for session start imports to complete (promise set up in constructor)
			await this.#importsCompletedPromise;
			this?.logger?.log( 'waitForReady: Imports completed' );

			// Handle skin initialization if needed
			await this.#handleSkinIfNeeded();
			this?.logger?.log( 'waitForReady: Skin handling complete' );

			// Execute final ready event
			await this.#executeOnReadyEvent();
			this?.logger?.log( 'waitForReady: EmbedManager ready' );

		} catch ( error ) {
			this?.logger?.error( 'waitForReady: EmbedManager initialization failed:', error );
			throw error;
		}
	};

	/**
	 * @description Wait for a specific Tag event to be dispatched
	 * @param {TagEvent} eventType
	 * @returns {Promise<void>}
	 */
	#waitForEvent = ( eventType: TagEvent ): Promise<void> => {
		return new Promise( ( resolve ) => {
			// Using IEventContextVO with timesToDispatch: 1 for auto-cleanup after event fires
			this.tag.eventDispatcher.addEventListener(
				{ type: eventType, timesToDispatch: 1 },
				() => {
					this?.logger?.log( `#waitForEvent: Received ${eventType}` );
					resolve();
				}
			);
		} );
	};

	/**
	 * @description Check if skin initialization is needed
	 * @returns {boolean}
	 */
	#needsSkin = (): boolean => {
		const skinMode = this?.tag?.model?.skinMode;
		return this?.params?.SKIN?.enabled === true
			&& ( skinMode === SkinMode.DESKTOP || skinMode === SkinMode.CUSTOM_MOBILE )
			&& this.params.SYSTEM.embedType !== EmbedType.HTML5;
	};

	/**
	 * @description Handle skin initialization if needed
	 * Preserves original logic: check autoplay, instantiate skin, wait for skin ready
	 * @returns {Promise<void>}
	 */
	#handleSkinIfNeeded = async (): Promise<void> => {
		// if skin is disabled => skip
		if ( this?.params?.SKIN?.enabled === false ) {
			this?.logger?.log( '#handleSkinIfNeeded: Skin disabled, skipping' );
			return;
		}

		// Filter per skin engine we are going to use
		switch ( this?.tag?.model?.skinMode ) {
			case SkinMode.DESKTOP: // Smart Tag's own desktop Skin (no Avia JS UI)
			case SkinMode.CUSTOM_MOBILE: { // the custom Mobile Skin (src/skin_mobile/) — same lifecycle as desktop Skin
				this?.logger?.log( `#handleSkinIfNeeded: ${this.tag.model.skinMode}, initializing skin` );

				// Check if autoplay was detected by SmartTag
				const shouldDetectAutoplay =
					this.utils.Type.isEmpty( window.SmartTag.autoplay )
					&&
					this.runtimeVO?.readyCallback?.detectAutoplay === true;

				// Detect autoplay if needed (preserving original sequence)
				if ( shouldDetectAutoplay ) {
					this?.logger?.log( '#handleSkinIfNeeded: Detecting autoplay capabilities' );
					await this.tag.controller.playerManager.playerAdapter.getAutoplayCapabilities();
				}

				// Start listening for the ready event BEFORE instantiating, not after — a defensive
				// ordering that costs nothing regardless of whether a given skin's ready dispatch
				// is sync or async, and protects against any skin implementation (present or
				// future) that ends up dispatching synchronously from its own constructor.
				const skinReadyPromise = this.#needsSkin() ? this.#waitForEvent( TagEvent.ON_CUSTOM_SKIN_READY ) : undefined;

				// Instantiate custom skin application
				this.#instantiateSkin();

				// Wait for skin to signal it's ready
				if ( skinReadyPromise ) {
					this?.logger?.log( '#handleSkinIfNeeded: Waiting for skin ready event' );
					await skinReadyPromise;
					this?.logger?.log( '#handleSkinIfNeeded: Skin ready' );
				}
				break;
			}

			case SkinMode.AVIA_UI: // avia-js-ui (default VTG player UI)
				this?.logger?.log( '#handleSkinIfNeeded: SkinMode.AVIA_UI, no skin needed' );
				// No additional setup needed for full player
				break;
		}
	};

	/**
	 * @description Instantiate skin application — desktop Skin or the custom Mobile Skin
	 * (src/skin_mobile/), per the current SkinMode
	 */
	#instantiateSkin = (): void => {
		const isCustomMobile = this.tag.model.skinMode === SkinMode.CUSTOM_MOBILE;
		const skinImport: IImportable = isCustomMobile
			? this.tag.model.config.tag.Imports.params.smart_tag.skin_mobile
			: this.tag.model.config.tag.Imports.params.smart_tag.skin;

		// check if skin application class reference exists in global DOM namespace
		const SkinClass: any = this.utils.Object.getDeepVal( window, skinImport?.namespace );

		switch ( SkinClass !== null ) {
			case true: // Skin class exists
				this.logger.groupCollapsed( 'instantiateSkin:', 'Create new instance of Skin class.' );
				// instantiate skin + store its reference in Tag instance — tag.view.skin only
				// requires ITagBridgeableSkin (onTagEvent), which both desktop Skin and the
				// custom Mobile Skin satisfy
				this.tag.view.skin = new SkinClass(
					{
						tag: this.tag, // reference to tag
						containerSelector: `[${TagDomAttribute.CONTENT}="${this.uuid}"]` // css selector for smart tag container element
					} as ISkinArgument
				);
				break;
			case false: // Skin class does not exist
				this?.logger?.warn( 'instantiateSkin:', 'Skin class does not exist:', SkinClass );
				break;
		}
	};

	/**
	 * @description Inform application about Tag ready state (we are ready for video load requests...)
	 * Simplified to just dispatch event - promise resolution handled by SmartTag.create()
	 * @returns {Promise<void>}
	 */
	#executeOnReadyEvent = async (): Promise<void> => {
		await this.utils.Delay.delay();
		// Dispatch ON_READY event - this will trigger the promise resolution in SmartTag.create()
		this.dispatchTagEvent( this.getTagEventVO( TagEvent.ON_READY, this.tag, this.getTagEventDataVO(), this, this?.name ) );
	};

	/**
	 * @description Tag application event handler
	 * Simplified - now only handles ON_METADATA_READY and ON_CUSTOM_SKIN_READY
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		// filter by event type
		switch ( event?.type ) {

			case TagEvent.ON_CUSTOM_SKIN_READY: // this event is dispatched from a skin application
				this.logger.groupEnd();
				this?.logger?.log( TagLoggerString.ON_TAG_EVENT, event?.type, event );

				// store reference to player container sent from custom skin, we are going to need it at time of vtg player instance creation...
				this.#playerContainer = event?.data?.playerContainer;
				break;

			case TagEvent.ON_METADATA_READY:
				this?.logger?.log( TagLoggerString.ON_TAG_EVENT, event?.type, event, this.params );

				// request to create player instance
				this.dispatchTagEvent(
					new TagEventVO(
						TagEvent.ON_CREATE_PLAYER_REQUEST,
						this.tag,
						{ playerContainer: this.playerContainer },
						this
					)
				);
				break;

		}
	};

}